import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ArrowsAiServicesAPI from 'dashboard/api/arrowsAi/services';
import {
  parseAPIErrorResponse,
  throwErrorMessage,
} from 'dashboard/store/utils/api';

const normalizeService = raw => ({
  id: Number(raw.id),
  name: raw.name || '',
  description: raw.description || '',
  price: raw.price || '',
  duration: raw.duration || '',
  createdAt: raw.created_at || raw.createdAt || new Date().toISOString(),
});

export function useArrowsAiServices(assistantId) {
  const { t } = useI18n();
  const services = ref([]);
  const isLoading = ref(false);
  let loadedAssistantId = null;

  const fetchServices = async (id, { force } = {}) => {
    if (!id) {
      services.value = [];
      loadedAssistantId = null;
      return;
    }
    if (!force && loadedAssistantId === id) return;

    isLoading.value = true;
    try {
      const { data } = await ArrowsAiServicesAPI.get({ assistantId: id });
      services.value = (Array.isArray(data) ? data : []).map(normalizeService);
      loadedAssistantId = id;
    } catch (error) {
      services.value = [];
      loadedAssistantId = id;
      const message = parseAPIErrorResponse(error);
      useAlert(
        typeof message === 'string'
          ? message
          : t('ARROWS_AI.SERVICES.FETCH_ERROR')
      );
    } finally {
      isLoading.value = false;
    }
  };

  watch(assistantId, id => fetchServices(id), { immediate: true });

  const createService = async payload => {
    try {
      const { data } = await ArrowsAiServicesAPI.create({
        assistantId: assistantId.value,
        payload,
      });
      const service = normalizeService(data);
      services.value = [service, ...services.value];
      return service;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const updateService = async (id, payload) => {
    try {
      const { data } = await ArrowsAiServicesAPI.update({
        assistantId: assistantId.value,
        id,
        payload,
      });
      const updated = normalizeService(data);
      services.value = services.value.map(item =>
        item.id === updated.id ? updated : item
      );
      return updated;
    } catch (error) {
      return throwErrorMessage(error);
    }
  };

  const deleteServices = async ids => {
    const uniqueIds = [...new Set(ids.map(Number))];
    try {
      await Promise.all(
        uniqueIds.map(id =>
          ArrowsAiServicesAPI.delete({ assistantId: assistantId.value, id })
        )
      );
      const idSet = new Set(uniqueIds);
      services.value = services.value.filter(item => !idSet.has(item.id));
    } catch (error) {
      await fetchServices(assistantId.value, { force: true });
      throwErrorMessage(error);
    }
  };

  const exportWorkbook = async () => {
    const { data } = await ArrowsAiServicesAPI.exportWorkbook({
      assistantId: assistantId.value,
    });
    const url = URL.createObjectURL(data);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = 'services.xlsx';
    window.document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const importServices = async file => {
    const { data } = await ArrowsAiServicesAPI.import({
      assistantId: assistantId.value,
      file,
    });
    const imported = (Array.isArray(data.imported) ? data.imported : []).map(
      normalizeService
    );
    services.value = [...imported.slice().reverse(), ...services.value];
    return data;
  };

  return {
    services: computed(() => services.value),
    isLoading: computed(() => isLoading.value),
    createService,
    updateService,
    deleteServices,
    importServices,
    exportWorkbook,
  };
}
